import {
   ChangeDetectionStrategy,
   ChangeDetectorRef,
   Component,
   ElementRef,
   Input,
   NgZone,
   OnDestroy,
   OnInit,
   ViewChild,
   ViewEncapsulation,
} from '@angular/core';
import { ControlValueAccessor, NgControl } from '@angular/forms';
import { Store } from '@ngrx/store';
import { Subject, fromEvent, merge } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import * as PromptDetailActions from '../../pages/prompt-details/state/prompt-detail.actions';

@Component({
    changeDetection: ChangeDetectionStrategy.OnPush,
    encapsulation: ViewEncapsulation.None,
    selector: 'ndt-file-upload',
    styleUrls: ['./file-upload.component.scss'],
    templateUrl: './file-upload.component.html',
    standalone: false
})
export class FileUploadComponent implements ControlValueAccessor, OnInit, OnDestroy {
   @ViewChild('fileInput', { static: true }) fileInput: ElementRef;

   @Input() label = 'Upload File';

   @Input() acceptedFileTypes = '';

   uploadedFile: File | null;

   disabled = false;

   onTouched: () => void;

   private onChange: (value: File | null) => void;

   dragging = false;

   dragoverTimer: number;

   fileType: string = '';

   private destroyed$ = new Subject<void>();

   constructor(
      private ngZone: NgZone,
      private changeDetectorRef: ChangeDetectorRef,
      control: NgControl,
      public store: Store
   ) {
      control.valueAccessor = this;
   }

   writeValue(file: File | null): void {
      this.uploadedFile = file;
      this.getFileType();
      if (!file) {
         (this.fileInput.nativeElement as HTMLInputElement).value = '';
      }
      this.changeDetectorRef.markForCheck();
   }

   getFileType() {
      if (this.acceptedFileTypes.includes('.wav') || this.acceptedFileTypes.includes('.mp3')) {
         this.fileType = 'audio';
      }
      if (this.acceptedFileTypes.includes('.xls') || this.acceptedFileTypes.includes('.xlsx')) {
         this.fileType = 'excel';
      }
   }

   registerOnChange(fn: (value: File | null) => void): void {
      this.onChange = fn;
   }

   registerOnTouched(fn: () => void): void {
      this.onTouched = fn;
   }

   setDisabledState(disabled: boolean): void {
      this.disabled = disabled;
   }

   ngOnInit(): void {
      this.ngZone.runOutsideAngular(() => {
         merge(
            fromEvent<DragEvent>(document, 'drag'),
            fromEvent<DragEvent>(document, 'dragstart'),
            fromEvent<DragEvent>(document, 'dragend'),
            fromEvent<DragEvent>(document, 'dragleave'),
            fromEvent<DragEvent>(document, 'dragenter'),
            fromEvent<DragEvent>(document, 'dragover')
         )
            .pipe(takeUntil(this.destroyed$))
            .subscribe((event: DragEvent) => {
               if (!this.dragging && event.type === 'dragenter') {
                  this.ngZone.run(() => {
                     this.dragging = true;
                     this.changeDetectorRef.markForCheck();
                  });
               } else if (this.dragging && event.type === 'dragover') {
                  clearTimeout(this.dragoverTimer);
                  this.dragoverTimer = window.setTimeout(() => {
                     this.ngZone.run(() => {
                        this.dragging = false;
                        this.changeDetectorRef.markForCheck();
                     });
                  }, 100);
               }
               event.preventDefault();
               event.stopPropagation();
            });

         fromEvent<DragEvent>(document, 'drop')
            .pipe(takeUntil(this.destroyed$))
            .subscribe((event: DragEvent) => {
               event.preventDefault();
               event.stopPropagation();

               let files: File[] | null = null;

               const dataTransfer = event.dataTransfer;
               if (dataTransfer && dataTransfer.files) {
                  files = Array.from(dataTransfer.files);
               }

               this.ngZone.run(() => {
                  if (files && files.length > 0) {
                     this.updateFile(files[0]);
                  }
                  this.dragging = false;
                  this.changeDetectorRef.markForCheck();
               });
            });
      });
   }

   ngOnDestroy(): void {
      this.destroyed$.next();
   }

   onUploadClick(event: Event) {
      event.preventDefault();
      this.fileInput.nativeElement.click();
   }

   onFileInputChange() {
      const files = (this.fileInput.nativeElement as HTMLInputElement).files;
      if (files && files[0]) {
         this.updateFile(files[0]);
      }
   }

   onAttachmentDelete() {
      this.updateFile(null);
   }

   private updateFile(file: File | null) {
      if (file) {
         let validTypes: string[] = [];
         if (this.acceptedFileTypes === '.wav, .mp3') validTypes = ['audio/mpeg', 'audio/wav'];
         else if (this.acceptedFileTypes === '.xls, .xlsx')
            validTypes = [
               'application/vnd.ms-excel',
               'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
            ];

         if (!validTypes.includes(file.type)) {
            this.store.dispatch(PromptDetailActions.uploadWrongFileType({ data: this.acceptedFileTypes }));
            return;
         }
      }

      this.writeValue(file);
      this.onChange(file);
      if (!file) {
         (this.fileInput.nativeElement as HTMLInputElement).value = '';
      }
   }
}
